import html2canvas from 'html2canvas';
import { sanitizeExportColors } from '../../../../../utils/exportColors';
import { jsPDF } from 'jspdf';
import { downloadBlob } from '../../toolPageUtils';
import {
    EXPORT_PAGE_HEIGHT_PX,
    EXPORT_PAGE_WIDTH_PX,
    buildEducationBoardResultBody,
    buildEducationBoardResultPdfPageHtml,
    educationBoardResultStyles,
    getEducationBoardResultLogoDataUrl,
    splitGradesAcrossPages,
} from './educationBoardResultTemplate';

const EXPORT_SCALE = 2;
const PDF_MARGIN_MM = 12;
const PDF_CONTENT_WIDTH_MM = 210 - PDF_MARGIN_MM * 2;
const PDF_CONTENT_HEIGHT_MM = 297 - PDF_MARGIN_MM * 2;
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;600;700&display=swap';

async function loadExportFonts() {
    const existing = document.querySelector('link[data-education-board-export-font]');
    if (!existing) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = FONT_HREF;
        link.setAttribute('data-education-board-export-font', 'true');
        document.head.appendChild(link);
        await new Promise((resolve) => {
            link.onload = resolve;
            link.onerror = resolve;
            setTimeout(resolve, 2500);
        });
    }

    try {
        await document.fonts.load('600 14px "Hind Siliguri"');
        await document.fonts.load('700 14px "Hind Siliguri"');
        await document.fonts.ready;
    } catch {
        // Continue with fallback fonts.
    }
}

function unwrapHtmlBody(html) {
    const match = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    return match ? match[1] : html;
}

function mountOffscreen(html, { width = EXPORT_PAGE_WIDTH_PX, height = null } = {}) {
    const wrapper = document.createElement('div');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.style.cssText = [
        'position:fixed',
        'top:0',
        'left:-12000px',
        `width:${width}px`,
        height ? `height:${height}px` : 'height:auto',
        'overflow:visible',
        'pointer-events:none',
        'z-index:-1',
        'background:#ffffff',
    ].join(';');

    const style = document.createElement('style');
    style.textContent = educationBoardResultStyles();
    wrapper.appendChild(style);

    const content = document.createElement('div');
    content.innerHTML = unwrapHtmlBody(html);
    wrapper.appendChild(content);
    document.body.appendChild(wrapper);

    return { wrapper, content };
}

async function waitForMountedAssets(root) {
    await loadExportFonts();

    const images = Array.from(root.querySelectorAll('img'));
    await Promise.all(images.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
        });
    }));

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
}

function measureHeight(root, selector) {
    const element = root.querySelector(selector);
    if (!(element instanceof HTMLElement)) return 0;
    return element.getBoundingClientRect().height;
}

async function measurePaginationMetrics(params) {
    const html = buildEducationBoardResultBody(params);
    const { wrapper, content } = mountOffscreen(html);

    try {
        await waitForMountedAssets(content);

        const grades = Array.isArray(params.result?.grades) ? params.result.grades : [];
        const firstRow = content.querySelector('.sk-grades tbody tr');

        return {
            pageHeight: EXPORT_PAGE_HEIGHT_PX,
            fixedTopHeight: (
                measureHeight(content, '.sk-header')
                + 18
                + measureHeight(content, '.sk-hero')
                + 18
                + measureHeight(content, '.sk-details-section')
                + 18
            ),
            footerHeight: measureHeight(content, '.sk-footer') + 18,
            gradesTitleHeight: measureHeight(content, '.sk-grades-section .sk-section-title') + 10,
            theadHeight: measureHeight(content, '.sk-grades thead'),
            rowHeight: firstRow instanceof HTMLElement ? firstRow.getBoundingClientRect().height : 34,
            gradesCount: grades.length,
        };
    } finally {
        wrapper.remove();
    }
}

async function capturePageCanvas(html) {
    const { wrapper, content } = mountOffscreen(html, {
        width: EXPORT_PAGE_WIDTH_PX,
        height: EXPORT_PAGE_HEIGHT_PX,
    });

    try {
        await waitForMountedAssets(content);
        const page = content.querySelector('.sk-export-page');
        if (!(page instanceof HTMLElement)) {
            throw new Error('Export page failed to render');
        }

        return await html2canvas(page, {
            scale: EXPORT_SCALE,
            width: EXPORT_PAGE_WIDTH_PX,
            height: EXPORT_PAGE_HEIGHT_PX,
            windowWidth: EXPORT_PAGE_WIDTH_PX,
            windowHeight: EXPORT_PAGE_HEIGHT_PX,
            useCORS: true,
            allowTaint: true,
            logging: false,
            // html2canvas can't parse oklch/lab/color() — rewrite any to rgb first.
            onclone: (d) => sanitizeExportColors(d.documentElement),
            backgroundColor: '#ffffff',
        });
    } finally {
        wrapper.remove();
    }
}

function addPageCanvasToPdf(canvas, pdf, pageIndex) {
    if (pageIndex > 0) pdf.addPage();

    pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.96),
        'JPEG',
        PDF_MARGIN_MM,
        PDF_MARGIN_MM,
        PDF_CONTENT_WIDTH_MM,
        PDF_CONTENT_HEIGHT_MM,
        undefined,
        'FAST',
    );
}

/**
 * @param {{ result: Record<string, any>, infoItems: Array<[string, any]>, labels: Record<string, any>, logoUrl?: string, language?: string }} params
 * @param {string} filename
 */
export async function downloadEducationBoardResultPdf(params, filename = 'education-board-result.pdf') {
    const logoDataUrl = params.logoUrl?.startsWith('data:image/')
        ? params.logoUrl
        : await getEducationBoardResultLogoDataUrl(
            window.location.origin,
            params.language,
        );

    const exportParams = {
        ...params,
        logoUrl: logoDataUrl,
    };

    const grades = Array.isArray(exportParams.result?.grades) ? exportParams.result.grades : [];
    const metrics = await measurePaginationMetrics(exportParams);
    const pageConfigs = splitGradesAcrossPages(grades, metrics);

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    for (let index = 0; index < pageConfigs.length; index += 1) {
        const pageHtml = buildEducationBoardResultPdfPageHtml(exportParams, pageConfigs[index]);
        const canvas = await capturePageCanvas(pageHtml);
        addPageCanvasToPdf(canvas, pdf, index);
    }

    downloadBlob(pdf.output('blob'), filename);
}
